import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isValidEmailUnknown } from './emailValidator';

interface LocalizedValue {
  value: string;
  languageCode: string;
}

interface FieldConfig {
  default?: string | boolean | LocalizedValue[];
  localised?: boolean;
  maxLength?: number;
  mode?: string;
  pattern?: string;
  required?: boolean;
  type?: string;
  validation?: (value: unknown) => boolean;
  validationMessage?: string;
  lowerCase?: boolean;
  upperCase?: boolean;
  disableTrim?: boolean;
}

type FieldsConfig = Record<string, FieldConfig>;

type ProcessedFields = Record<string, FieldConfig>;

type DefaultValues = Record<
  string,
  string | boolean | LocalizedValue[] | undefined
>;

type Properties = Record<
  string,
  {
    maxLength?: number;
    mode?: string;
    pattern?: string;
    required?: boolean;
    type?: string;
    disabled?: boolean;
  }
>;

type Errors = Record<string, string>;

type Values = Record<string, unknown>;

type ProcessedValues = Record<string, unknown>;

interface UseFormProps {
  changesRequired?: boolean;
  fields?: FieldsConfig;
  locales?: string[];
  onSubmit?: (processedValues: ProcessedValues) => Promise<void>;
  onSubmitHook?: (processedValues: ProcessedValues) => void;
  onSubmitHookEnded?: boolean;
  requiredLocales?: string[];
}

interface UseFormReturn {
  changeValues: (newValues: Values) => void;
  disableSubmit: boolean;
  edited: boolean;
  errors: Errors;
  handleBlur: (params: { name: string }) => void;
  handleChange: (params: { name: string; value: unknown }) => void;
  handleSubmit: (event?: React.FormEvent) => Promise<void>;
  missingRequired: boolean;
  properties: Properties;
  reset: () => void;
  setDefaults: (newValues: Values) => void;
  setRequiredLocales: (newRequiredLocales: string[]) => void;
  submitting: boolean;
  validate: () => { valid: boolean; processedValues: ProcessedValues };
  values: Values;
}

const compareValue = (valueA: unknown, valueB: unknown): boolean => {
  if (valueA === valueB) return true;

  const typeA = typeof valueA;
  const typeB = typeof valueB;

  if (typeA !== typeB) return false;

  if (typeA === 'object')
    return JSON.stringify(valueA) === JSON.stringify(valueB);

  return valueA === valueB;
};

const getDefaultValues = (fields: ProcessedFields): DefaultValues =>
  Object.entries(fields).reduce<DefaultValues>((acc, [key, value]) => {
    // TODO: Differentiate between inputs and checkboxes/switches
    acc[key] = 'default' in value ? value.default : '';
    return acc;
  }, {});

const getProcessedFields = (
  fields: FieldsConfig,
  locales?: string[],
  requiredLocales?: string[]
): ProcessedFields =>
  Object.entries(fields).reduce<ProcessedFields>((acc, [key, value]) => {
    if (value.localised && locales?.length && locales.length > 0) {
      locales.forEach((locale) => {
        if (
          value.required &&
          requiredLocales &&
          !requiredLocales.includes(locale)
        ) {
          acc[`${key}#${locale}`] = {
            ...value,
            required: false,
          };
        } else {
          acc[`${key}#${locale}`] = value;
        }
      });
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});

const getProperties = (processedFields: ProcessedFields): Properties =>
  Object.entries(processedFields).reduce<Properties>((acc, [key, value]) => {
    acc[key] = {
      maxLength: value.maxLength,
      mode: value.mode,
      pattern: value.pattern,
      required: value.required,
      type: value.type,
    };
    return acc;
  }, {});

const normalize = (value: unknown, field: FieldConfig): unknown => {
  if (typeof value !== 'string') {
    return value;
  }

  let processedValue = value.replace(/—|–/g, '-');

  if (field.lowerCase) {
    processedValue = processedValue.toLowerCase();
  }
  if (field.upperCase) {
    processedValue = processedValue.toUpperCase();
  }

  if (!field.disableTrim) {
    processedValue = processedValue.trim();
  }

  return processedValue;
};

const useForm = ({
  changesRequired,
  fields = {},
  locales,
  onSubmit,
  onSubmitHook,
  onSubmitHookEnded,
  requiredLocales,
}: UseFormProps): UseFormReturn => {
  const [processedFields, setProcessedFields] = useState<ProcessedFields>(
    getProcessedFields(fields, locales, requiredLocales)
  );
  const [defaultValues, setDefaultValues] = useState<DefaultValues>(
    getDefaultValues(processedFields)
  );
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [edited, setEdited] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [missingRequired, setMissingRequired] = useState(true);
  const [properties, setProperties] = useState<Properties>(
    getProperties(processedFields)
  );
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState<Values>(defaultValues);

  const { t } = useTranslation();

  useEffect(() => {
    setProperties(getProperties(processedFields));
  }, [processedFields]);

  useEffect(() => {
    const { missingRequired, noModified } = Object.entries(
      processedFields
    ).reduce(
      (acc, [key, value]) => {
        if (value.required && !values[key]) {
          acc.missingRequired = true;
        }
        if (!compareValue(values[key], defaultValues[key])) {
          acc.noModified = false;
        }
        return acc;
      },
      { missingRequired: false, noModified: true }
    );

    setEdited(!noModified);
    setMissingRequired(missingRequired);
    setDisableSubmit(
      missingRequired || (Boolean(changesRequired) && noModified)
    );
  }, [processedFields, values, defaultValues, changesRequired]);

  const enableSubmit = (): void => {
    setSubmitting(false);
    setProperties((prevProperties) =>
      Object.entries(prevProperties).reduce<Properties>((acc, [key, value]) => {
        const { disabled: _disabled, ...other } = value;
        acc[key] = {
          ...other,
        };
        return acc;
      }, {})
    );
  };

  useEffect(() => {
    if (onSubmitHookEnded) {
      enableSubmit();
    }
  }, [onSubmitHookEnded]);

  const validateField = (
    field: FieldConfig,
    value: unknown
  ): string | undefined => {
    if (!field.required && !value) {
      return undefined;
    }

    const validationFunction =
      field.validation ||
      (field.type === 'email' ? isValidEmailUnknown : undefined);

    if (validationFunction && !validationFunction(value)) {
      return field.validationMessage || t('errors.formValidationError');
    }

    return undefined;
  };

  const validate = (): { valid: boolean; processedValues: ProcessedValues } => {
    const { errors, hasErrors, processedValues } = Object.entries(
      processedFields
    ).reduce<{
      errors: Errors;
      hasErrors: boolean;
      processedValues: ProcessedValues;
    }>(
      (acc, [name, field]) => {
        const processedValue = normalize(values[name], field);

        if (name in fields) {
          acc.processedValues[name] = processedValue;
        } else {
          const [originalName, locale] = name.split('#');
          if (originalName in fields && locales?.includes(locale)) {
            if (!acc.processedValues[originalName]) {
              acc.processedValues[originalName] = [];
            }
            if (processedValue && typeof processedValue === 'string') {
              (acc.processedValues[originalName] as LocalizedValue[]).push({
                value: processedValue,
                languageCode: locale,
              });
            }
          }
        }

        const error = validateField(field, processedValue);
        if (error) {
          acc.errors[name] = error;
          acc.hasErrors = true;
        }

        return acc;
      },
      { errors: {}, hasErrors: false, processedValues: {} }
    );

    setErrors(errors);
    return { valid: !hasErrors, processedValues };
  };

  const handleSubmit = async (event?: React.FormEvent): Promise<void> => {
    if (event) {
      event.preventDefault();
    }

    const { valid, processedValues } = validate();

    if (valid && !disableSubmit) {
      setSubmitting(true);
      // Disable all fields while the form is being submitted
      setProperties((prevProperties) =>
        Object.entries(prevProperties).reduce<Properties>(
          (acc, [key, value]) => {
            acc[key] = {
              ...value,
              disabled: true,
            };
            return acc;
          },
          {}
        )
      );

      if (onSubmitHook) {
        onSubmitHook(processedValues);
      } else if (onSubmit) {
        try {
          await onSubmit(processedValues);
        } finally {
          enableSubmit();
        }
      }
    }
  };

  const handleBlur = ({ name }: { name: string }): void => {
    const field = processedFields[name];
    const processedValue = normalize(values[name], field);

    if (processedValue) {
      const error = validateField(field, processedValue);
      if (error) {
        setErrors({ ...errors, [name]: error });
      }
    }
  };

  const handleChange = ({
    name,
    value,
  }: {
    name: string;
    value: unknown;
  }): void => {
    const { [name]: _error, ...otherErrors } = errors;
    setErrors({ ...otherErrors });
    setValues({ ...values, [name]: value });
  };

  const changeValues = (newValues: Values): void => {
    setValues((values) => {
      const changedValues = Object.entries(newValues).reduce<Values>(
        (acc, [key, value]) => {
          if (key in fields) {
            if (fields[key].localised) {
              (value as LocalizedValue[]).forEach(
                ({ value: newValue, languageCode }) => {
                  const newKey = `${key}#${languageCode}`;
                  if (newKey in values) {
                    acc[newKey] = newValue || '';
                  }
                }
              );
            } else {
              acc[key] = value;
            }
          }
          return acc;
        },
        {}
      );
      return { ...values, ...changedValues };
    });
    setErrors({});
  };

  const setDefaults = (newValues: Values): void => {
    setDefaultValues((values) => {
      const changedValues = Object.entries(newValues).reduce<DefaultValues>(
        (acc, [key, value]) => {
          if (key in fields) {
            if (fields[key].localised) {
              (value as LocalizedValue[]).forEach(
                ({ value: newValue, languageCode }) => {
                  const newKey = `${key}#${languageCode}`;
                  if (newKey in values) {
                    acc[newKey] = newValue || '';
                  }
                }
              );
            } else {
              acc[key] = value as
                string | boolean | LocalizedValue[] | undefined;
            }
          }
          return acc;
        },
        {}
      );
      return { ...values, ...changedValues };
    });
    changeValues(newValues);
  };

  const setRequiredLocales = (newRequiredLocales: string[]): void => {
    setProcessedFields(getProcessedFields(fields, locales, newRequiredLocales));
  };

  const reset = (): void => {
    const newValues = getDefaultValues(processedFields);
    setDefaultValues(newValues);
    setValues(newValues);
    setErrors({});
  };

  return {
    changeValues,
    disableSubmit,
    edited,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    missingRequired,
    properties,
    reset,
    setDefaults,
    setRequiredLocales,
    submitting,
    validate,
    values,
  };
};

export default useForm;
